import { useCallback, useEffect, useMemo } from 'react';
import { useField, useForm } from '@shopify/react-form';
import { useSession } from '@sdk/session/SessionContext';
import { Attachment } from '@sdk/attachments/types';
import { DEFAULT_GROUP_HEADERS } from '@sdk/components/forms/personaPickers/GroupedPersonaPicker/groupedPersonaPicker.consts';
import { usePersonaCollection } from '@sdk/personaCollection/PersonaCollectionContext';
import { PersonaPickerOption } from '@sdk/components/forms/personaPickers/PersonaPicker/personaPicker.types';
import { createPersonaPickerRequiredValidator } from '@sdk/components/forms/personaPickers/utils/createPersonaPickerRequiredValidator';

import { useDeferredPromise } from '@web/common/hooks/useDeferredPromise';
import { usePostAnnouncement } from '@web/announcements/hooks/usePostAnnouncement';
import { useWorkingByDayPersonaOption } from '@web/common/hooks/useWorkingByDayPersonaOption/useWorkingByDayPersonaOption';
import { useMentionPersonas } from '@web/common/hooks/useMentionPersonas/useMentionPersonas';

import { mapPersonaToSuggestions } from './newAnnouncementForm.utils';

export const usePersonaData = () => {
  const { workingTomorrowGroup, workingTodayGroup } = useWorkingByDayPersonaOption();

  const {
    currentUser: {
      org: { name },
    },
  } = useSession();

  const personaCollection = usePersonaCollection();

  const groupHeaders = DEFAULT_GROUP_HEADERS;
  groupHeaders.everyone.title = name;

  const { pickerPersonas, memberIds } = useMemo(
    () => mapPersonaToSuggestions(personaCollection),
    [personaCollection]
  );

  const mentionPersonas = useMentionPersonas(memberIds);

  return {
    groupHeaders,
    mentionPersonas,
    pickerPersonas: pickerPersonas.concat(workingTodayGroup, workingTomorrowGroup).filter(Boolean),
  };
};

const notEmptyValidator = (value: string | any[]) => (value.length ? undefined : 'required');

export const useNewAnnouncementForm = (onClose: () => void) => {
  // @INFO a workaround to let ProgressButton work with form submit handler
  const { defer, deferRef } = useDeferredPromise<any>();
  useEffect(() => {
    defer();
  }, [defer]);
  const personaCollection = usePersonaCollection();
  const { isLoading, mutateAsync } = usePostAnnouncement();
  const { possiblyUnwrapOption, workingTodayGroupId, workingTomorrowGroupId } =
    useWorkingByDayPersonaOption();
  // @INFO workingToday and workingTomorrow groups are shown only if not empty, hence they are always valid
  const pickerValidator = createPersonaPickerRequiredValidator(personaCollection, [
    workingTodayGroupId,
    workingTomorrowGroupId,
  ]);

  const attachmentsField = useField<Attachment[]>([]);
  const contentField = useField<string>({
    value: '',
    validates: notEmptyValidator,
  });
  const recipientsField = useField<PersonaPickerOption[]>({
    value: [],
    validates: pickerValidator,
  });
  const titleField = useField<string>({
    value: '',
    validates: notEmptyValidator,
  });

  const { dirty, fields, submit, reset, validate, submitErrors } = useForm({
    fields: {
      attachments: attachmentsField,
      content: contentField,
      recipients: recipientsField,
      title: titleField,
    },
    onSubmit: async ({ attachments, content, recipients, title }) => {
      try {
        await mutateAsync({
          attachments,
          content,
          personas: recipients.flatMap(possiblyUnwrapOption).map((r) => ({ id: r.id })),
          title,
        });

        reset();
        onClose();
        deferRef.resolve(true);

        return { status: 'success' };
      } catch (e) {
        deferRef.reject(e);

        return { status: 'fail', errors: [] };
      }
    },
  });

  // @INFO shopify-form does not re-validate form on change
  const isFormValid = useMemo(() => {
    const result = validate();

    return !result.length;

    // eslint-disable-next-line
  }, [recipientsField.value, titleField.value, contentField.value, validate]);

  const isActive = !isLoading && dirty && isFormValid;
  const hasSubmitErrors = submitErrors.length > 0;

  const retardedSubmit = useCallback(async () => {
    if (isLoading) return;
    submit();

    return deferRef.promise;
  }, [deferRef?.promise, isLoading, submit]);

  return {
    fields,
    hasSubmitErrors,
    isActive,
    retardedSubmit,
    submit,
  };
};
